"use client";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import { popupOptionType } from "@/types/ModuleOptionsType";
import { columnSize } from "@/utils/layout/columnSize.ts";
import { customSanitize } from "@/utils/customSanitize.ts";
import { popupType } from "@/types/OptionType";

import styles from "./PopUp.module.scss";
import { useAppContext } from "../../context/AppContext";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import {
  containsScriptTag,
  parseScriptContent,
} from "@/utils/scriptInjection.ts";
import { ExtendedConsentManagement } from "../ExtendedConsentManagement/ExtendedConsentManagement";
import { useLenis } from "lenis/react";
import { FocusTrap } from "@aleksundshantu/aunds-utils-react/accessibility";
import { CustomSVG } from "../_primitives/CustomSVG/CustomSVG.client";
import { AccordionItems } from "@/modules/Accordion/AccordionItems";
import { FadeInUp } from "../Animation";

interface PopUpProps {
  extraClassName?: string;
  popupOptions: popupOptionType;
  popupData: popupType;
}

export const AddProperty = (
  elements: HTMLCollectionOf<HTMLElement>,
  property: string,
  value: string,
) => {
  for (let i = 0; i < elements.length; i++) {
    const element = elements[i];
    if (element.style) element.style.setProperty(property, value);
  }
};

const PopUp = forwardRef<HTMLDivElement, PopUpProps>(
  ({ extraClassName = "", popupOptions, popupData, ...props }, ref) => {
    const innerRef = useRef<HTMLDivElement | null>(null);

    const {
      columns,
      show_popup,
      box: { show_border_radius, show_box_shadow, show_card_box },
    } = popupOptions ?? {};

    const {
      activePopupContent,
      openPopup,
      setActivePopupContent,
      setopenPopup,
    } = useAppContext();
    const { getIcon, getLabel } = useGlobalContext();

    const { content, type_consent_management, aria_description } =
      popupData ?? {};
    const themeOptionsContent = (
      <p
        dangerouslySetInnerHTML={{
          __html: customSanitize(content),
        }}
      />
    );

    const isScript = containsScriptTag(content);

    const lenis = useLenis();

    useImperativeHandle(ref, () => innerRef.current as HTMLDivElement);

    useEffect(() => {
      if (isScript && openPopup) {
        const tid = setTimeout(() => {
          parseScriptContent(content, "popup", "popup-script");
          clearTimeout(tid);
        }, 100);
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isScript, openPopup]);

    useEffect(() => {
      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          setActivePopupContent(null);
          setopenPopup(false);
        }
      };

      document.addEventListener("keydown", handleEscape);
      return () => {
        document.removeEventListener("keydown", handleEscape);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      const htmlTag = document.documentElement;
      const scrollBarWidth = window.innerWidth - htmlTag.clientWidth;
      const tagsToAddmargin = document.getElementsByClassName(
        "scrollbar-width",
      ) as HTMLCollectionOf<HTMLElement>;
      if (openPopup) {
        if (scrollBarWidth !== 0) {
          AddProperty(
            tagsToAddmargin,
            "width",
            `calc(100% - ${scrollBarWidth}px)`,
          );
          htmlTag.style.marginRight = `${scrollBarWidth}px`;
        }
        lenis?.stop();
      } else {
        AddProperty(tagsToAddmargin, "width", `100%`);
        htmlTag.style.marginRight = `unset`;
        lenis?.start();
      }
    }, [openPopup, lenis]);

    if (!show_popup) return null;

    return (
      <>
        {openPopup && (
          // eslint-disable-next-line jsx-a11y/no-autofocus -- ARIA APG: focus must move into dialog on open
          <FocusTrap autoFocus>
            <div
              id="popup"
              ref={innerRef}
              {...props}
              role="dialog"
              aria-modal="true"
              // aria-labelledby="popup-title" // ID modal title ??
              aria-describedby={aria_description ?? "popup-script"}
              //! please dont remove scrollbar-width class

              className={`${styles["pop-up"]} ${extraClassName} scrollbar-width`}
            >
              <button
                onClick={() => {
                  setActivePopupContent(null);
                  setopenPopup(false);
                }}
                type="button"
                aria-label={getLabel("modal_close")}
                className={`${styles["close-btn"]} navbar-menu-burger-close`}
              >
                <CustomSVG
                  src={getIcon("close")}
                  wrapper={"span"}
                  aria-hidden="true"
                  className="is-flex"
                />
                <span className={styles.label}>{getLabel("close")}</span>
              </button>
              <div className="container">
                <div className="columns is-centered">
                  <div className={`column ${columnSize(columns)}`}>
                    <div
                      id="popup-script"
                      className={`${styles["popup-content"]}  ${show_card_box ? "is-box" : ""} ${show_border_radius && show_card_box ? "has-border-radius" : ""} ${show_box_shadow && show_card_box ? "has-box-shadow" : ""}`}
                    >
                      <div
                        data-lenis-prevent
                        className={styles["accordion-popup-content"]}
                      >
                        {activePopupContent?.type === "accordion" ? (
                          activePopupContent.categories.map(
                            (category, index) => (
                              <section
                                className={styles["accordion-popup-category"]}
                                key={`${category.name}-${index}`}
                              >
                                <div className="columns is-centered">
                                  <div className="column is-12-tablet is-10-desktop">
                                    {category.name && (
                                      <FadeInUp>
                                        <span
                                          className={`h2-small ${styles["accordion-popup-headline"]}`}
                                        >
                                          {category.name}
                                        </span>
                                      </FadeInUp>
                                    )}
                                    <AccordionItems
                                      elements={category.elements}
                                      border_line={{
                                        color: "",
                                        height: 1,
                                      }}
                                      separator="border"
                                      box_color=""
                                      moduleIndex={index}
                                      showExtendedLink={false}
                                      buttonType="secondary"
                                    />
                                  </div>
                                </div>
                              </section>
                            ),
                          )
                        ) : type_consent_management ? (
                          <ExtendedConsentManagement
                            type_consent_management={type_consent_management}
                          >
                            {themeOptionsContent}
                          </ExtendedConsentManagement>
                        ) : (
                          themeOptionsContent
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </FocusTrap>
        )}
      </>
    );
  },
);

export default PopUp;

PopUp.displayName = "PopUp";
